import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
    Snackbar,
    Tab,
    Tabs
} from '@mui/material';
import { WhatsApp, Send, AttachFile } from '@mui/icons-material';
import { getWhatsAppAccounts, sendWhatsAppMessage } from '../redux/slices/whatsappSlice';
import axios from 'axios';

export default function WhatsappManager() {
    const dispatch = useDispatch();
    const accounts = useSelector((state) => state.whatsapp.accounts || []);
    const loading = useSelector((state) => state.whatsapp.loading);
    const token = localStorage.getItem('token');

    const [selectedAccount, setSelectedAccount] = useState('');
    const [currentTab, setCurrentTab] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Message form
    const [messageForm, setMessageForm] = useState({ number: '', text: '' });

    // Media form
    const [mediaForm, setMediaForm] = useState({ number: '', file: null, caption: '' });

    useEffect(() => {
        dispatch(getWhatsAppAccounts());
    }, [dispatch]);

    // Auto-select first connected account
    useEffect(() => {
        if (accounts.length > 0 && !selectedAccount) {
            const connectedAccount = accounts.find(acc => acc.status === 'connected');
            if (connectedAccount) {
                setSelectedAccount(connectedAccount._id);
            }
        }
    }, [accounts, selectedAccount]);

    const handleSendMessage = async () => {
        if (!selectedAccount || !messageForm.number || !messageForm.text) {
            setSnackbar({ open: true, message: 'يرجى ملء جميع الحقول', severity: 'error' });
            return;
        }

        try {
            await dispatch(sendWhatsAppMessage({
                accountId: selectedAccount,
                number: messageForm.number,
                text: messageForm.text
            })).unwrap();

            setSnackbar({ open: true, message: '✅ تم إرسال الرسالة بنجاح!', severity: 'success' });
            setMessageForm({ number: '', text: '' });
        } catch (error) {
            setSnackbar({ open: true, message: error || 'فشل إرسال الرسالة', severity: 'error' });
        }
    };

    const handleSendMedia = async () => {
        if (!selectedAccount || !mediaForm.number || !mediaForm.file) {
            setSnackbar({ open: true, message: 'يرجى اختيار ملف ورقم الهاتف', severity: 'error' });
            return;
        }

        try {
            const formData = new FormData();
            formData.append('accountId', selectedAccount);
            formData.append('number', mediaForm.number);
            formData.append('media', mediaForm.file);
            if (mediaForm.caption) formData.append('caption', mediaForm.caption);

            const config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            };

            await axios.post('https://social-auto-r8we.onrender.com/api/whatsapp/send-media', formData, config);

            setSnackbar({ open: true, message: '✅ تم إرسال الملف بنجاح!', severity: 'success' });
            setMediaForm({ number: '', file: null, caption: '' });
        } catch (error) {
            setSnackbar({ open: true, message: error.response?.data?.message || 'فشل إرسال الملف', severity: 'error' });
        }
    };

    const connectedAccounts = accounts.filter(acc => acc.status === 'connected');
    const selectedAccountData = accounts.find(acc => acc._id === selectedAccount);

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', borderRadius: 3, p: { xs: 3, md: 4 }, mb: 3, color: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <WhatsApp sx={{ fontSize: { xs: 32, md: 40 } }} />
                    <Box>
                        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                            إدارة WhatsApp
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            إرسال الرسائل والوسائط عبر WhatsApp
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {connectedAccounts.length === 0 ? (
                <Alert severity="warning">
                    <Typography variant="h6">لا توجد حسابات مربوطة</Typography>
                    <Typography>
                        يرجى الذهاب إلى صفحة "Accounts Manager" → تبويب "WhatsApp" لإضافة حساب جديد
                    </Typography>
                </Alert>
            ) : (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card sx={{ boxShadow: 3 }}>
                            <CardContent>
                                <FormControl fullWidth sx={{ mb: 3 }}>
                                    <InputLabel>اختر حساب WhatsApp</InputLabel>
                                    <Select
                                        value={selectedAccount}
                                        onChange={(e) => setSelectedAccount(e.target.value)}
                                        label="اختر حساب WhatsApp"
                                    >
                                        {connectedAccounts.map((account) => (
                                            <MenuItem key={account._id} value={account._id}>
                                                {account.number || account.phoneNumber || account._id}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {selectedAccountData && (
                                    <Alert severity="success" sx={{ mb: 3 }}>
                                        الرسائل سترسل من: <strong>{selectedAccountData.number}</strong>
                                    </Alert>
                                )}

                                <Tabs
                                    value={currentTab}
                                    onChange={(e, v) => setCurrentTab(v)}
                                    sx={{ mb: 3 }}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    allowScrollButtonsMobile
                                >
                                    <Tab icon={<Send />} label="رسالة نصية" />
                                    <Tab icon={<AttachFile />} label="رسالة وسائط" />
                                </Tabs>

                                {/* Text Message Tab */}
                                {currentTab === 0 && (
                                    <Box>
                                        <Typography variant="h6" gutterBottom>إرسال رسالة نصية</Typography>
                                        <TextField
                                            fullWidth
                                            label="رقم الهاتف"
                                            placeholder="+962791234567"
                                            value={messageForm.number}
                                            onChange={(e) => setMessageForm({ ...messageForm, number: e.target.value })}
                                            sx={{ mb: 2 }}
                                        />
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={4}
                                            label="نص الرسالة"
                                            value={messageForm.text}
                                            onChange={(e) => setMessageForm({ ...messageForm, text: e.target.value })}
                                            sx={{ mb: 2 }}
                                        />
                                        <Button
                                            variant="contained"
                                            startIcon={<Send />}
                                            onClick={handleSendMessage}
                                            disabled={loading}
                                            sx={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
                                        >
                                            {loading ? 'جاري الإرسال...' : 'إرسال'}
                                        </Button>
                                    </Box>
                                )}

                                {/* Media Message Tab */}
                                {currentTab === 1 && (
                                    <Box>
                                        <Typography variant="h6" gutterBottom>إرسال ملف وسائط</Typography>
                                        <TextField
                                            fullWidth
                                            label="رقم الهاتف"
                                            placeholder="+962791234567"
                                            value={mediaForm.number}
                                            onChange={(e) => setMediaForm({ ...mediaForm, number: e.target.value })}
                                            sx={{ mb: 2 }}
                                        />
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            fullWidth
                                            sx={{ mb: 2 }}
                                        >
                                            {mediaForm.file ? mediaForm.file.name : 'اختر ملف'}
                                            <input
                                                type="file"
                                                hidden
                                                onChange={(e) => setMediaForm({ ...mediaForm, file: e.target.files[0] })}
                                                accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
                                            />
                                        </Button>
                                        <TextField
                                            fullWidth
                                            label="تعليق (اختياري)"
                                            value={mediaForm.caption}
                                            onChange={(e) => setMediaForm({ ...mediaForm, caption: e.target.value })}
                                            sx={{ mb: 2 }}
                                        />
                                        <Button
                                            variant="contained"
                                            startIcon={<Send />}
                                            onClick={handleSendMedia}
                                            disabled={loading}
                                            sx={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
                                        >
                                            {loading ? 'جاري الإرسال...' : 'إرسال الملف'}
                                        </Button>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
